import {
    MAIN_WINDOW_ID,
    getStore,
    emptyMenuDefinition,
    placeholderMenuDefinition,
} from '../store/menuDefinitionStore';
import type { MenuDefinition } from 'owa-acui';
import type { MenuDefinitionStoreNames } from '../store/menuDefinitionStore';
import { mutatorAction } from 'satcheljs';

/**
 * Creates an entrypoint in the MenuDefinition map manager for a given projectionTabId.
 * Used specifically for projection popout scenarios.
 */
export const createMenuDefinitionStoreMutator: (projectionTabId: string | undefined) => void =
    mutatorAction(
        'createMenuDefinitionStore',
        function createMenuDefinitionStore(projectionTabId: string | undefined): void {
            if (projectionTabId === undefined) {
                // We create a store on-boot for the main window (undefined), so return early here.
                return;
            }
            getStore().menuDefinitionMapManager.set(projectionTabId, {
                MenuDefinitionMap: new Map<MenuDefinitionStoreNames, () => MenuDefinition>(),
            });
        }
    );

/**
 * Removes the entrypoint in the MenuDefinition map manager for a given projectionTabId.
 * Used specifically for projection popout scenarios.
 */
export const removeMenuDefinitionStoreMutator: (projectionTabId: string | undefined) => void =
    mutatorAction(
        'removeMenuDefinitionStore',
        function removeMenuDefinitionStore(projectionTabId: string | undefined): void {
            if (projectionTabId === undefined) {
                // We never want to delete the main window store, so we return early here.
                return;
            }

            const { menuDefinitionMapManager } = getStore();
            const projectionMap = menuDefinitionMapManager.get(projectionTabId);

            if (projectionMap) {
                projectionMap.MenuDefinitionMap.clear();
            }

            menuDefinitionMapManager.delete(projectionTabId);
        }
    );

/**
 * Resets a store's menuDefinition back to one of two "reset" values.
 * Call this mutator in the `onAfterMenuDismiss` callback of a Ribbon control only if you explicitly
 * do not want the menu to be loaded even after the first click.
 * An example of when this is useful is Flag/Unflag and Snooze, of which their menuDefinition has a call
 * to observableNow(), which causes the Ribbon to render every 60 seconds even if nothing is happening.
 */
export const resetMenuDefinitionMutator: (
    storeName: MenuDefinitionStoreNames,
    projectionTabId?: string
) => void = mutatorAction(
    'resetMenuDefinition',
    function resetMenuDefinition(
        storeName: MenuDefinitionStoreNames,
        projectionTabId?: string
    ): void {
        const id = projectionTabId || MAIN_WINDOW_ID;
        const { menuDefinitionMapManager } = getStore();
        const rootMap = menuDefinitionMapManager.get(id);
        const storeMap = rootMap && rootMap.MenuDefinitionMap.get(storeName);

        if (storeMap) {
            const resetMenu = () =>
                id === MAIN_WINDOW_ID ? emptyMenuDefinition : placeholderMenuDefinition;

            const isMenuReset = storeMap() === resetMenu();

            // If menu is already reset, avoid resetting it again
            if (!isMenuReset) {
                rootMap.MenuDefinitionMap.set(storeName, resetMenu);
            }
        }
    }
);
